import { useResetOnClose } from "@/lib/hooks/useResetOnClose"
import React, { FC, Dispatch, SetStateAction, useState, useEffect } from "react"
import {
  Dropdown,
  Modal,
  MultiSelectDropdown,
  Table,
  Tabs,
  Toggle,
} from "@/components"
import {
  CarrierRatesType,
  CarrierType,
  DirectRatesType,
  mexicoMarketSearchInstance,
  MexicoMarketSearchType,
  MexicoMarketType,
  PortOfEntryType,
  TransloadPartnerType,
} from "@/lib/types"
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  EyeIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline"
import { convertEquipmentType, dollarUS } from "@/lib/services"
import { Header } from "@/lib/static"
import { useForm } from "react-hook-form"
import { VehicleCard } from "../user/VehicleCard"

type Props = {
  popup: boolean
  carrierListing: { data: CarrierType[] }
  setPopup: Dispatch<SetStateAction<boolean>>
  listingData: { mexicoMarketSearch: MexicoMarketSearchType[] }
  listOfMexicoMarket: { data: MexicoMarketType[] }
  listOfPortOfEnteries: { data: PortOfEntryType[] }
  handleFormUpdate: (form: CarrierRatesType) => void
  handleDirectRatesUpdate: (form: DirectRatesType) => void
  handlePortOfEntryUpdate: (form: PortOfEntryType) => void
  handleCarrierUpdate: (form: CarrierType) => void
  handleTransloadUpdate: (form: TransloadPartnerType) => void
  transloadCarrierList: { id: string; transloadCarrier: string }[]
  header: {
    name: string
    width: string
  }[]
  popupCarrierRates: boolean
  setPopupCarrierRates: Dispatch<SetStateAction<boolean>>
  popupDirectRates: boolean
  setPopupDirectRates: Dispatch<SetStateAction<boolean>>
  popupPortOfEntry: boolean
  setPopupPortOfEntry: Dispatch<SetStateAction<boolean>>
  popupTransload: boolean
  setPopupTransload: Dispatch<SetStateAction<boolean>>
  popupCarrier: boolean
  setPopupCarrier: Dispatch<SetStateAction<boolean>>
}

const tabs = [
  {
    identifier: "carrier-rates",
    name: "Carrier Rates",
  },
  {
    identifier: "direct-rates",
    name: "Direct Rates",
  },
  {
    identifier: "port-Of-Entry",
    name: "Port Of Entry",
  },
  {
    identifier: "carriers",
    name: "Carriers",
  },
  {
    identifier: "transload-partners",
    name: "Transload Partners",
  },
]

export const MexicoMarketSearch: FC<Props> = (props) => {
  const [transload, setTransload] = useState(false)
  const [onboarded, setOnboarded] = useState(false)
  const [carrierRatesId, setCarrierRatesId] = useState("")
  const [directRatesId, setDirectRatesId] = useState("")
  const [entryId, setEntryId] = useState("")
  const [carrierId, setCarrierId] = useState("")
  const [transloadPartnerId, setTransloadPartnerId] = useState("")

  const [mexicoMarketName, setMexicoMarketName] = useState("")
  const [CTPAT, setCTPAT] = useState(false)
  const [hazmat, setHazmat] = useState(false)
  const [isCarrier, setIsCarrier] = useState(false)
  const [portOfEntryId, setPortOfEntryId] = useState("")
  const [data, setData] = useState<MexicoMarketSearchType>(
    mexicoMarketSearchInstance,
  )
  const [selectedTab, setSelectedTab] = useState("carrier-rates")
  const [portOfEntries, setPortOfEntries] = useState<
    { id: string; name: string }[]
  >([])
  const [selectedMexicoMarket, setSelectedMexicoMarket] = useState<
    {
      id: string
      name: string
    }[]
  >([])
  const [selectedPortOfEntry, setSelectedPortOfEntry] = useState<
    {
      id: string
      name: string
    }[]
  >([])
  const [carrierSearch, setCarrierSearch] = useState<{
    id?: string
    name: string
  }>({
    id: "",
    name: "",
  })
  const [carrier, setCarrier] = useState<{
    id: string
    name: string
  }>({
    id: "",
    name: "",
  })
  const {
    register: registerCarrierRates,
    handleSubmit: handleSubmitCarrierRates,
    setValue: setValueCarrierRates,
    reset: resetCarrierRates,
    formState: { errors: errorsCarrierRates },
  } = useForm<CarrierRatesType>()
  const {
    register: registerDirectRates,
    handleSubmit: handleSubmitDirectRates,
    setValue: setValueDirectRates,
    reset: resetDirectRates,
    formState: { errors: errorsDirectRates },
  } = useForm<DirectRatesType>()
  const {
    register: registerPortOfEntry,
    handleSubmit: handleSubmitPortOfEntry,
    setValue: setValuePortOfEntry,
    reset: resetPortOfEntry,
    formState: { errors: errorsPortOfEntry },
  } = useForm<PortOfEntryType>()
  const {
    register: registerCarrier,
    handleSubmit: handleSubmitCarrier,
    setValue: setValueCarrier,
    reset: resetCarrier,
    formState: { errors: errorsCarrier },
  } = useForm<CarrierType>()
  const {
    register: registerTransloadPartner,
    handleSubmit: handleSubmitTransloadPartner,
    setValue: setValueTransloadPartner,
    reset: resetTransloadPartner,
    formState: { errors: errorsTransloadPartner },
  } = useForm<TransloadPartnerType>()

  const populateCarrierRatesData = (e: CarrierRatesType) => {
    setValueCarrierRates("id", e.id)
    setValueCarrierRates("equipment", e.equipment)
    setValueCarrierRates("mexicoMarketId", e.mexicoMarketId)
    setCarrier({ id: e.carrierId, name: e.carrierName })
    setValueCarrierRates("type", e.type)
    setValueCarrierRates("freightCost", e.freightCost)
    props.setPopupCarrierRates(true)
  }

  const populateDirectRatesData = (e: DirectRatesType) => {
    const selectedMexicoMarkets = e.mexicoMarket.filter(
      (entry) => entry != undefined && e.id,
    )
    const selectedPortOfEnteries = e.portOfEntry.filter(
      (entry) => entry != undefined && e.id,
    )
    setSelectedMexicoMarket(
      selectedMexicoMarkets as { id: string; name: string }[] | [],
    )
    setSelectedPortOfEntry(
      selectedPortOfEnteries as { id: string; name: string }[] | [],
    )
    setValueDirectRates("id", e.id)
    setValueDirectRates("mexicoMarketId", e.mexicoMarketId)
    setValueDirectRates("domesticState", e.domesticState)
    setValueDirectRates("crossingFee", e.crossingFee)
    setValueDirectRates("ratePerMile", e.ratePerMile)
    setValueDirectRates("type", e.type)
    props.setPopupDirectRates(true)
  }

  const populatePortOfEntryData = (e: PortOfEntryType) => {
    setValuePortOfEntry("id", e.id)
    setValuePortOfEntry("name", e.name)
    setValuePortOfEntry("city", e.city)
    setValuePortOfEntry("state", e.state)
    setValuePortOfEntry("zipCode", e.zipCode)
    props.setPopupPortOfEntry(true)
  }
  const populateCarrierData = (e: CarrierType) => {
    setValueCarrier("id", e.id)
    setValueCarrier("name", e.name)
    setValueCarrier("USDOT", e.USDOT == null ? 0 : e.USDOT)
    setValueCarrier("MCMX", e.MCMX == "" ? "-" : e.MCMX)
    setValueCarrier("contactName", e.contactName == null ? "-" : e.contactName)
    setValueCarrier("email", e.email == null ? "-" : e.email)
    setValueCarrier("phone", e.phone == null ? "-" : e.phone)
    setValueCarrier("equipment", e.equipment)
    setOnboarded(e.onboarded)
    setCTPAT(e.CTPAT)
    setHazmat(e.hazmat)
    setTransload(e.transload)
    setIsCarrier(e.carrier)
    const selectedPortOfEnteries =
      e.portOfEntries != undefined
        ? e.portOfEntries.filter((entry) => entry != undefined && e.id)
        : []
    setSelectedPortOfEntry(
      selectedPortOfEnteries as { id: string; name: string }[] | [],
    )
    props.setPopupCarrier(true)
  }
  const populateTransloadData = (e: TransloadPartnerType) => {
    setValueTransloadPartner("id", e.id)
    setValueTransloadPartner("equipmentType", e.equipmentType)
    setValueTransloadPartner(
      "transloadCarrier",
      e.transloadCarrier == null ? "-" : e.transloadCarrier,
    )
    setValueTransloadPartner("transloadCost", e.transloadCost)
    setPortOfEntryId(e.portOfEntryId)
    setValueTransloadPartner("portOfEntryId", e.portOfEntryId)
    props.setPopupTransload(true)
  }

  useEffect(() => {
    const carrierRatesData = props.listingData.mexicoMarketSearch.filter(
      (item) =>
        item.SBCarrierRates.filter((item) => item.id === carrierRatesId),
    )
    const directRatesData = props.listingData.mexicoMarketSearch.filter(
      (item) => item.DirectRates.filter((item) => item.id === directRatesId),
    )

    const portOfEntryData = props.listingData.mexicoMarketSearch.find(
      (item) => item.portOfEntry.id === entryId,
    )

    const carriersData = props.listingData.mexicoMarketSearch.filter((item) =>
      item.portOfEntry.carriers.some((carrier) => carrier.id === carrierId),
    )

    const transloadPartnersData = props.listingData.mexicoMarketSearch.filter(
      (item) =>
        item.portOfEntry.TransloadPartners.filter(
          (item) => item.id === transloadPartnerId,
        ),
    )

    if (
      carrierRatesData.length > 0 &&
      carrierRatesData[0].SBCarrierRates.length !== 0
    ) {
      setData((prev) => {
        return {
          ...prev,
          SBCarrierRates: carrierRatesData[0].SBCarrierRates,
        }
      })
    }

    if (
      directRatesData.length > 0 &&
      directRatesData[0].DirectRates.length !== 0
    ) {
      setData((prev) => {
        return {
          ...prev,
          DirectRates: directRatesData[0].DirectRates,
        }
      })
    }

    if (portOfEntryData?.portOfEntry) {
      data.portOfEntry.id = portOfEntryData.portOfEntry.id
      data.portOfEntry.name = portOfEntryData.portOfEntry.name
      data.portOfEntry.city = portOfEntryData?.portOfEntry.city
      data.portOfEntry.state = portOfEntryData?.portOfEntry.state
      data.portOfEntry.zipCode = portOfEntryData?.portOfEntry.zipCode
    }

    if (
      carriersData.length > 0 &&
      carriersData[0].portOfEntry.carriers.length !== 0
    ) {
      setData((prev) => ({
        ...prev,
        portOfEntry: {
          ...prev.portOfEntry,
          carriers: [...carriersData[0].portOfEntry.carriers],
        },
      }))
    }

    if (
      transloadPartnersData.length > 0 &&
      transloadPartnersData[0].portOfEntry.TransloadPartners.length !== 0
    ) {
      setData((prev) => {
        return {
          ...prev,
          portOfEntry: {
            ...prev.portOfEntry,
            TransloadPartners:
              transloadPartnersData[0].portOfEntry.TransloadPartners,
          },
        }
      })
    }
  }, [props.listingData.mexicoMarketSearch])

  const handleSearchChange = (value: string) => {
    setCarrierSearch({ name: value })
  }

  const onSubmitCarrierRates = (data: CarrierRatesType) => {
    data.carrierId = carrier.id
    data.carrierName = carrier.name

    props.handleFormUpdate(data)
  }
  const onSubmitDirectRates = (data: DirectRatesType) => {
    data.crossingFee = parseInt(String(data.crossingFee))
    data.ratePerMile = parseInt(String(data.ratePerMile))
    data.mexicoMarket = selectedMexicoMarket.map((e) => e.id)
    data.portOfEntry = selectedPortOfEntry.map((e) => e.id)

    data.portOfEntrySelection =
      selectedPortOfEntry.length > 0 ? "SELECTED" : "ALL"
    data.marketSelection = selectedMexicoMarket.length > 0 ? "SELECTED" : "ALL"

    props.handleDirectRatesUpdate(data)
  }

  const onSubmitPortOfEntry = (data: PortOfEntryType) => {
    props.handlePortOfEntryUpdate(data)
  }

  const onSubmitCarrier = (data: CarrierType) => {
    data.onboarded = onboarded
    data.CTPAT = CTPAT
    data.hazmat = hazmat
    data.transload = transload
    data.carrier = isCarrier
    data.USDOT = Number(data.USDOT)
    const portOfEntryId = portOfEntries.map((portOfEntry) => portOfEntry.id)

    data.portOfEntry = [...portOfEntryId]
    props.handleCarrierUpdate(data)
  }

  const onSubmitTransload = (data: TransloadPartnerType) => {
    data.portOfEntryId = portOfEntryId
    data.transloadCost = parseInt(String(data.transloadCost))

    props.handleTransloadUpdate(data)
  }

  useResetOnClose(props.popupCarrierRates, () => {
    resetCarrierRates({
      equipment: "",
      mexicoMarketId: "",
      type: "",
      freightCost: undefined,
      id: undefined,
    })
    setCarrier({ id: "", name: "" })
  })

  useResetOnClose(props.popupDirectRates, () => {
    resetDirectRates({
      mexicoMarketId: "",
      domesticState: "",
      type: "",
      crossingFee: undefined,
      ratePerMile: undefined,
      id: undefined,
    })
    setPortOfEntries([])
    setSelectedPortOfEntry([])
    setSelectedMexicoMarket([])
  })

  useResetOnClose(props.popupPortOfEntry, () => {
    resetPortOfEntry({
      name: undefined,
      city: undefined,
      state: undefined,
      zipCode: undefined,
      id: undefined,
    })
  })

  useResetOnClose(props.popupCarrier, () => {
    resetCarrier({
      name: "",
      USDOT: undefined,
      MCMX: "",
      contactName: "",
      email: "",
      phone: "",
      equipment: "",
      hazmat: false,
      transload: false,
      carrier: false,
      id: undefined,
    })
    setOnboarded(false)
    setCTPAT(false)
    setHazmat(false)
    setTransload(false)
    setIsCarrier(false)
    setPortOfEntries([])
    setSelectedPortOfEntry([])
  })

  useResetOnClose(props.popupTransload, () => {
    resetTransloadPartner({
      equipmentType: "",
      transloadCarrier: "",
      portOfEntryId: "",
      transloadCost: undefined,
      id: undefined,
    })
    setPortOfEntryId("")
  })

  return (
    <>
      <Modal
        open={props.popup}
        close={() => {
          props.setPopup(false)
        }}
        width="medium"
        title={mexicoMarketName}
      >
        <div>
          <div className="mb-3">
            {/* <h1 className="dark:text-primary-10 text-light-2 px-2 py-4">{mexicoMarketName}</h1> */}
            <Tabs
              searchPage={true}
              selectTab={setSelectedTab}
              tabs={tabs}
              selectedTab={selectedTab}
            />
          </div>
          {selectedTab == "carrier-rates" ? (
            <div className="m-4">
              <Table
                header={Header.CarrierRates}
                headerAlign="text-left"
                lastheadingAlign="text-right"
              >
                {data.SBCarrierRates.length > 0 ? (
                  data.SBCarrierRates.map((e, i) => (
                    <tr key={i} className="text-left">
                      <td className={"whitespace-nowrap p-4 w-64 truncate"}>
                        {e.carrierName}
                      </td>
                      <td className="whitespace-nowrap p-4 w-52">
                        {e.type == null
                          ? "-"
                          : e.type == "Southbound"
                          ? "Southbound"
                          : e.type == "Northbound"
                          ? "Northbound"
                          : null}
                      </td>
                      <td className={"whitespace-nowrap p-4 w-52"}>
                        {convertEquipmentType(e.equipment) == "Van"
                          ? "Dry Van"
                          : convertEquipmentType(e.equipment)}
                      </td>
                      <td className={"whitespace-nowrap p-4 w-52"}>
                        {e.mexicoMarket == undefined
                          ? "-"
                          : e.mexicoMarket.name}
                      </td>
                      <td className={"whitespace-nowrap p-4 w-52"}>
                        {dollarUS.format(e.freightCost)}
                      </td>
                      <td className={"whitespace-nowrap p-4 w-52"}>
                        {e.transloadPartner == null
                          ? "-"
                          : dollarUS.format(
                            e.freightCost + e.transloadPartner.transloadCost,
                          )}
                      </td>
                      <td className={"whitespace-nowrap p-4 w-24"}>
                        <div className="flex justify-end gap-2">
                          <PencilSquareIcon
                            className="w-4 h-4 text-accent cursor-pointer"
                            onClick={() => {
                              setCarrierRatesId(e.id)
                              populateCarrierRatesData(e)
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className="hover:bg-white hover:bg-opacity-5 flex py-4 justify-center text-xs dark:text-primary-10 text-light-2">
                    No records found
                  </tr>
                )}
              </Table>
            </div>
          ) : selectedTab == "direct-rates" ? (
            <div className="m-4">
              <Table
                header={Header.DirectRates}
                headerAlign="text-left"
                lastheadingAlign="text-right"
              >
                {data.DirectRates.length > 0 ? (
                  data.DirectRates.map((e, i) => (
                    <tr key={i} className="text-left">
                      <td className={"whitespace-nowrap p-4 w-52"}>
                        {e.type == null
                          ? "-"
                          : e.type == "Southbound"
                          ? "Southbound"
                          : e.type == "Northbound"
                          ? "Northbound"
                          : null}
                      </td>
                      <td className={"whitespace-nowrap p-4 w-52"}>
                        {e.mexicoMarket.length > 0
                          ? e.mexicoMarket.map((item: any, i) => (
                            <div key={i}>
                              {item.name}
                              {e.mexicoMarket.length > 1 &&
                                i < e.mexicoMarket.length - 1 &&
                                ","}
                            </div>
                          ))
                          : "-"}
                      </td>
                      <td className={"whitespace-nowrap p-4 w-52"}>
                        {e.portOfEntry.length > 0
                          ? e.portOfEntry.map((item: any, i) => (
                            <div key={i}>
                              {item.name}{" "}
                              {e.portOfEntry.length > 1 &&
                                i < e.portOfEntry.length - 1 &&
                                ","}
                            </div>
                          ))
                          : "-"}
                      </td>
                      <td className={"whitespace-nowrap p-4 w-52"}>
                        {e.domesticState}
                      </td>
                      <td className={"whitespace-nowrap p-4 w-24"}>
                        <div className="flex justify-end gap-2">
                          <PencilSquareIcon
                            className="w-4 h-4 text-accent cursor-pointer"
                            onClick={() => {
                              setDirectRatesId(e.id)
                              populateDirectRatesData(e)
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className="hover:bg-white hover:bg-opacity-5 flex py-4 justify-center text-xs dark:text-primary-10 text-light-2">
                    No records found
                  </tr>
                )}
              </Table>
            </div>
          ) : selectedTab == "port-Of-Entry" ? (
            <div className="m-4">
              <Table
                header={Header.PortOfEntry}
                headerAlign="text-left"
                lastheadingAlign="text-right"
              >
                <tr className="text-left">
                  <td className={"whitespace-nowrap p-4 w-52"}>
                    {data.portOfEntry.name}
                  </td>
                  <td className={"whitespace-nowrap p-4 w-52"}>
                    {data.portOfEntry.city}
                  </td>
                  <td className={"whitespace-nowrap p-4 w-52"}>
                    <p>{data.portOfEntry.state}</p>
                  </td>
                  <td className={"whitespace-nowrap p-4 w-52"}>
                    <p>{data.portOfEntry.zipCode}</p>
                  </td>
                  <td className={"whitespace-nowrap p-4 w-24 text-right"}>
                    <div className="flex justify-end gap-2">
                      <PencilSquareIcon
                        className="w-4 h-4 text-accent cursor-pointer"
                        onClick={() => {
                          setEntryId(data.portOfEntry.id as string)
                          populatePortOfEntryData(
                            data.portOfEntry as PortOfEntryType,
                          )
                        }}
                      />
                    </div>
                  </td>
                </tr>
              </Table>
            </div>
          ) : selectedTab == "carriers" ? (
            <div className="m-4">
              <Table
                header={Header.Carrier}
                headerAlign="text-left"
                lastheadingAlign="text-right"
              >
                {data.portOfEntry.carriers.length > 0 ? (
                  data.portOfEntry.carriers.map((e, i) => (
                    <tr key={i} className="text-left">
                      <td className={"whitespace-nowrap p-4 w-80 truncate"}>
                        {e.name}
                      </td>
                      <td className={"whitespace-nowrap p-4 w-24"}>
                        {e.USDOT == null ? "-" : e.USDOT}
                      </td>
                      <td className={"whitespace-nowrap p-4 w-28"}>
                        {e.MCMX == "" ? "-" : e.MCMX}
                      </td>
                      <td className={"whitespace-nowrap p-4 w-32"}>
                        <div className="flex justify-center">
                          {e.onboarded ? (
                            <CheckCircleIcon className="w-7 h-7 text-green-600 cursor-pointer" />
                          ) : (
                            <ExclamationCircleIcon className="w-7 h-7 text-red-600 cursor-pointer" />
                          )}
                        </div>
                      </td>
                      <td className={"whitespace-nowrap p-4 w-40"}>
                        {e.contactName == null ? "-" : e.contactName}
                      </td>
                      <td className={"whitespace-nowrap p-4 w-80"}>
                        {e.email == null ? "-" : e.email}
                      </td>
                      <td className={"whitespace-nowrap p-4 w-28"}>
                        {e.phone == null ? "-" : e.phone}
                      </td>
                      <td className={"whitespace-nowrap p-4 w-72"}>
                        {convertEquipmentType(e.equipment)}
                      </td>
                      <td className={"whitespace-nowrap p-4 w-20"}>
                        <div className="flex justify-center">
                          {e.CTPAT ? (
                            <CheckCircleIcon className="w-7 h-7 text-green-600 cursor-pointer" />
                          ) : (
                            <ExclamationCircleIcon className="w-7 h-7 text-red-600 cursor-pointer" />
                          )}
                        </div>
                      </td>
                      <td className={"whitespace-nowrap p-4 w-24"}>
                        <div className="flex justify-center">
                          {e.hazmat ? (
                            <CheckCircleIcon className="w-7 h-7 text-green-600 cursor-pointer" />
                          ) : (
                            <ExclamationCircleIcon className="w-7 h-7 text-red-600 cursor-pointer" />
                          )}
                        </div>
                      </td>
                      <td className={"whitespace-nowrap p-4 w-28"}>
                        <div className="flex justify-center">
                          {e.transload ? (
                            <CheckCircleIcon className="w-7 h-7 text-green-600 cursor-pointer" />
                          ) : (
                            <ExclamationCircleIcon className="w-7 h-7 text-red-600 cursor-pointer" />
                          )}
                        </div>
                      </td>
                      <td className={"whitespace-nowrap p-4 w-24"}>
                        <div className="flex justify-center">
                          {e.carrier ? (
                            <CheckCircleIcon className="w-7 h-7 text-green-600 cursor-pointer" />
                          ) : (
                            <ExclamationCircleIcon className="w-7 h-7 text-red-600 cursor-pointer" />
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap p-4 w-24 flex justify-end items-center gap-2">
                        <PencilSquareIcon
                          className="w-4 h-4 text-accent cursor-pointer"
                          onClick={() => {
                            setCarrierId(e.id)
                            populateCarrierData(e)
                          }}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className="hover:bg-white hover:bg-opacity-5 flex py-4 justify-center text-xs dark:text-primary-10 text-light-2">
                    No records found
                  </tr>
                )}
              </Table>
            </div>
          ) : selectedTab == "transload-partners" ? (
            <div className="m-4">
              <Table
                header={Header.TransloadPartner}
                headerAlign="text-left"
                lastheadingAlign="text-right"
              >
                {data.portOfEntry.TransloadPartners.length > 0 ? (
                  data.portOfEntry.TransloadPartners.map((e, i) => (
                    <tr key={i} className="text-left">
                      <td className={"whitespace-nowrap p-4 w-52"}>
                        {convertEquipmentType(e.equipmentType)}
                      </td>
                      <td className={"whitespace-nowrap p-4 w-52"}>
                        {e.transloadCarrier == null ? "-" : e.transloadCarrier}
                      </td>
                      <td className={"whitespace-nowrap p-4 w-52"}>
                        {dollarUS.format(e.transloadCost)}
                      </td>
                      <td className={"whitespace-nowrap p-4 w-52"}>
                        <p>
                          {e.portOfEntry !== undefined && e.portOfEntry.name}
                        </p>
                      </td>
                      <td className={"whitespace-nowrap p-4 w-24"}>
                        <div className="flex justify-end gap-2">
                          <PencilSquareIcon
                            className="w-4 h-4 text-accent cursor-pointer"
                            onClick={() => {
                              setTransloadPartnerId(e.id)
                              populateTransloadData(e)
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className="hover:bg-white hover:bg-opacity-5 flex py-4 justify-center text-xs dark:text-primary-10 text-light-2">
                    No records found
                  </tr>
                )}
              </Table>
            </div>
          ) : null}
        </div>
      </Modal>
      <Modal
        open={props.popupCarrierRates}
        close={() => props.setPopupCarrierRates(false)}
        width="medium"
        title="Update Carrier Rates"
      >
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-1">
              <input
                className="hidden vehicle_radio_customize"
                type="radio"
                id="vehicle_card"
                value="DRY_VAN"
                {...registerCarrierRates("equipment", { required: true })}
              />
              <label htmlFor="vehicle_card">
                <VehicleCard image="/images/dry-van.svg" name="Dry Van" />
              </label>
            </div>
            <div className="col-span-1">
              <input
                className="hidden vehicle_radio_customize"
                type="radio"
                id="vehicle_card1"
                value="FLATBED"
                {...registerCarrierRates("equipment", { required: true })}
              />
              <label htmlFor="vehicle_card1">
                <VehicleCard image="/images/flatbed.svg" name="Flatbed" />
              </label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <fieldset>
              <label className="text-xs text-accent">Mexico Market</label>
              <select
                className={
                  (errorsCarrierRates.equipment
                    ? "border-red-400"
                    : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-primary-1 bg-white mt-1 outline-none dark:text-primary-2 border-2 rounded dark:placeholder:text-zinc-500 placeholder:text-xs"
                }
                {...registerCarrierRates("mexicoMarketId", {
                  required: true,
                })}
              >
                <option value="" hidden>
                  Select Mexico Market
                </option>
                {props.listOfMexicoMarket.data.map((e, i) => (
                  <option key={i} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </select>
            </fieldset>
            <fieldset>
              <label className="text-xs text-accent">Carrier</label>
              <div className="mt-1">
                <Dropdown
                  listingData={props.carrierListing.data}
                  selected={carrier}
                  setSelected={setCarrier}
                />
              </div>
            </fieldset>
            <fieldset>
              <label className="text-xs text-accent">Type</label>
              <select
                className={
                  (errorsCarrierRates.type
                    ? "border-red-400"
                    : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-primary-1 bg-white mt-1 outline-none dark:text-primary-10 border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                }
                placeholder="Enter Rate Per Mile"
                {...registerCarrierRates("type", { required: true })}
              >
                <option value="" hidden>
                  Select Type
                </option>
                <option value="Southbound">Southbound</option>
                <option value="Northbound">Northbound</option>
              </select>
            </fieldset>
            <fieldset>
              <label className="text-xs text-accent">Freight Cost</label>
              <input
                className={
                  (errorsCarrierRates.freightCost
                    ? "border-red-400"
                    : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white mt-1 outline-none dark:text-white border-2 rounded dark:placeholder:text-zinc-500 placeholder:text-light-2 placeholder:text-xs"
                }
                type="number"
                placeholder="Enter Freight Cost"
                {...registerCarrierRates("freightCost", { required: true })}
              />
            </fieldset>
          </div>
        </div>
        <button
          className="bg-primary-8 px-4 py-2 rounded font-medium w-full mt-2 dark:text-primary-1 text-primary-1"
          onClick={handleSubmitCarrierRates(onSubmitCarrierRates)}
          type="button"
        >
          Submit
        </button>
      </Modal>
      <Modal
        open={props.popupDirectRates}
        close={() => props.setPopupDirectRates(false)}
        width="medium"
        title="Update Direct Rates"
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-2">
              <MultiSelectDropdown
                title="Mexico Market"
                selectedList={selectedMexicoMarket}
                setSelectedList={setSelectedMexicoMarket}
                listingData={props.listOfMexicoMarket}
                selectedIds={selectedMexicoMarket}
              />
              <MultiSelectDropdown
                title="Port Of Entries"
                selectedList={selectedPortOfEntry}
                setSelectedList={setSelectedPortOfEntry}
                listingData={props.listOfPortOfEnteries}
                selectedIds={selectedPortOfEntry}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">Crossing Fee</label>
                <input
                  className={
                    (errorsDirectRates.crossingFee
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="number"
                  placeholder="Enter Crossing Fee"
                  {...registerDirectRates("crossingFee", { required: true })}
                />
              </fieldset>
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">Domestic State</label>
                <input
                  className={
                    (errorsDirectRates.domesticState
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="text"
                  placeholder="Enter Domestic State"
                  {...registerDirectRates("domesticState", { required: true })}
                />
              </fieldset>
            </div>
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Type</label>
              <select
                className={
                  (errorsDirectRates.type
                    ? "border-red-400"
                    : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-primary-1 bg-white text-light-2 mt-1 outline-none dark:text-primary-10 border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                }
                placeholder="Enter Rate Per Mile"
                {...registerDirectRates("type", { required: true })}
              >
                <option value="" hidden>
                  Select Type
                </option>
                <option value="Southbound">Southbound</option>
                <option value="Northbound">Northbound</option>
              </select>
            </fieldset>
          </div>
          <button
            className="bg-primary-8 px-4 py-2 rounded font-medium w-full mt-2 dark:text-primary-1 text-primary-1"
            onClick={handleSubmitDirectRates(onSubmitDirectRates)}
            type="button"
          >
            Submit
          </button>
        </div>
      </Modal>
      <Modal
        open={props.popupPortOfEntry}
        close={() => props.setPopupPortOfEntry(false)}
        width="small"
        title='Update Port of Entry'
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-3 ">
            <div className="grid grid-cols-2 gap-2">
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">Name</label>
                <input
                  className={
                    (errorsPortOfEntry.name
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="text"
                  placeholder="Enter Name"
                  {...registerPortOfEntry("name", { required: true })}
                />
              </fieldset>
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">City</label>
                <input
                  className={
                    (errorsPortOfEntry.city
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="text"
                  placeholder="Enter City"
                  {...registerPortOfEntry("city", { required: true })}
                />
              </fieldset>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">State</label>
                <input
                  className={
                    (errorsPortOfEntry.state
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="text"
                  placeholder="Enter State"
                  {...registerPortOfEntry("state", { required: true })}
                />
              </fieldset>
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">Zip Code</label>
                <input
                  className={
                    (errorsPortOfEntry.zipCode
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="text"
                  placeholder="Enter Zipcode"
                  {...registerPortOfEntry("zipCode", { required: true })}
                />
              </fieldset>
            </div>
          </div>
          <button
            className="bg-primary-8 px-4 py-2 rounded font-medium w-full mt-2 dark:text-primary-1 text-primary-1"
            onClick={handleSubmitPortOfEntry(onSubmitPortOfEntry)}
            type="button"
          >
            Submit
          </button>
        </div>
      </Modal>
      <Modal
        open={props.popupCarrier}
        close={() => props.setPopupCarrier(false)}
        width="medium"
        title='Update Carrier'
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-2">
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">Name</label>
                <input
                  className={
                    (errorsCarrier.name
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="text"
                  placeholder="Enter Name"
                  {...registerCarrier("name", { required: true })}
                />
              </fieldset>
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">USDOT</label>
                <input
                  className={
                    (errorsCarrier.USDOT
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="number"
                  placeholder="Enter USDOT"
                  {...registerCarrier("USDOT", { required: true })}
                />
              </fieldset>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">MCMX</label>
                <input
                  className={
                    (errorsCarrier.MCMX
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="text"
                  placeholder="Enter MCMX"
                  {...registerCarrier("MCMX", { required: true })}
                />
              </fieldset>
              <div className="flex justify-between">
                <div className="flex flex-col justify-evenly pl-1">
                  <label className="text-xs dark:text-primary-8 text-light-1">Onboarded</label>
                  <Toggle
                    toggled={onboarded}
                    onClick={(state: boolean) => setOnboarded(state)}
                  />
                </div>
                <div className="flex flex-col justify-evenly pl-1">
                  <label className="text-xs dark:text-primary-8 text-light-1">CTPAT</label>
                  <Toggle
                    toggled={CTPAT}
                    onClick={(state: boolean) => setCTPAT(state)}
                  />
                </div>
                <div className="flex flex-col justify-evenly pl-1">
                  <label className="text-xs dark:text-primary-8 text-light-1">Hazmat</label>
                  <Toggle
                    toggled={hazmat}
                    onClick={(state: boolean) => setHazmat(state)}
                  />
                </div>
                <div className="flex flex-col justify-evenly pl-1">
                  <label className="text-xs dark:text-primary-8 text-light-1">Transload</label>
                  <Toggle
                    toggled={transload}
                    onClick={(state: boolean) => setTransload(state)}
                  />
                </div>
                <div className="flex flex-col justify-evenly pl-1">
                  <label className="text-xs dark:text-primary-8 text-light-1">Carrier</label>
                  <Toggle
                    toggled={isCarrier}
                    onClick={(state: boolean) => setIsCarrier(state)}
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">Contact Name</label>
                <input
                  className={
                    (errorsCarrier.contactName
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="text"
                  placeholder="Enter ContactName"
                  {...registerCarrier("contactName", { required: true })}
                />
              </fieldset>
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">Email</label>
                <input
                  className={
                    (errorsCarrier.email
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="email"
                  placeholder="Enter Email"
                  {...registerCarrier("email", { required: true })}
                />
              </fieldset>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col ">
                <fieldset>
                  <label className="text-xs dark:text-primary-8 text-light-1">Equipment</label>
                  <input
                    className={
                      (errorsCarrier.equipment
                        ? "border-red-400"
                        : "dark:border-secondary-2") +
                      " w-full text-sm p-2 dark:bg-primary-1 bg-white mt-1 outline-none dark:text-primary-2 text-light-2 border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                    }
                    {...registerCarrier("equipment", { required: true })}
                  />
                </fieldset>
                <fieldset>
                  <label className="text-xs dark:text-primary-8 text-light-1">Phone</label>
                  <input
                    className={
                      (errorsCarrier.phone
                        ? "border-red-400"
                        : "dark:border-secondary-2") +
                      " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                    }
                    type="text"
                    placeholder="Enter Phone"
                    {...registerCarrier("phone", { required: true })}
                  />
                </fieldset>
              </div>
              <MultiSelectDropdown
                title="Port Of Entries"
                selectedList={portOfEntries}
                setSelectedList={setPortOfEntries}
                selectedIds={selectedPortOfEntry}
                listingData={props.listOfPortOfEnteries}
              />
            </div>
          </div>
          <button
            className="bg-primary-8 px-4 py-2 rounded font-medium w-full mt-2 dark:text-primary-1 text-primary-1"
            onClick={handleSubmitCarrier(onSubmitCarrier)}
            type="button"
          >
            Submit
          </button>
        </div>
      </Modal>
      <Modal
        open={props.popupTransload}
        close={() => props.setPopupTransload(false)}
        width="small"
        title="Update Transload Partner"
      >
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-1">
              <input
                className="hidden vehicle_radio_customize"
                type="radio"
                id="vehicle_card"
                value="DRY_VAN"
                {...registerTransloadPartner("equipmentType", {
                  required: true,
                })}
              />
              <label htmlFor="vehicle_card">
                <VehicleCard image="/images/dry-van.svg" name="Dry Van" />
              </label>
            </div>
            <div className="col-span-1">
              <input
                className="hidden vehicle_radio_customize"
                type="radio"
                id="vehicle_card1"
                value="FLATBED"
                {...registerTransloadPartner("equipmentType", {
                  required: true,
                })}
              />
              <label htmlFor="vehicle_card1">
                <VehicleCard image="/images/flatbed.svg" name="Flatbed" />
              </label>
            </div>
          </div>
          <div className="flex flex-col gap-3 mt-3">
            <div className="grid grid-cols-2 gap-2">
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">
                  Transload Carrier
                </label>
                <input
                  className={
                    (errorsTransloadPartner.transloadCarrier
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="text"
                  placeholder="Enter Transload Carrier"
                  {...registerTransloadPartner("transloadCarrier", {
                    required: true,
                  })}
                />
              </fieldset>
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">Transload Cost</label>
                <input
                  className={
                    (errorsTransloadPartner.transloadCost
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="number"
                  placeholder="Enter Transload Cost"
                  {...registerTransloadPartner("transloadCost", {
                    required: true,
                  })}
                />
              </fieldset>
            </div>

            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Port Of Entry</label>
              <select
                className={
                  (errorsTransloadPartner.portOfEntryId
                    ? "border-red-400"
                    : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-primary-1 bg-white text-light-2 outline-none dark:text-primary-10 border-2 rounded placeholder:text-xs "
                }
                placeholder="Enter Port Of Entry"
                value={portOfEntryId}
                onChange={(e) => setPortOfEntryId(e.target.value)}
              >
                <option value="" hidden>
                  Select Port Of Entry
                </option>
                {props.listOfPortOfEnteries.data.map((e, i) => (
                  <option value={e.id} key={i}>
                    {e.name}
                  </option>
                ))}
              </select>
            </fieldset>
          </div>
          <button
            className="bg-primary-8 px-4 py-2 rounded font-medium w-full mt-2 dark:text-primary-1 text-primary-1"
            onClick={handleSubmitTransloadPartner(onSubmitTransload)}
            type="button"
          >
            Submit
          </button>
        </div>
      </Modal>
      <div className="m-4">
        <Table
          header={props.header}
          headerAlign="text-left"
          lastheadingAlign="text-right"
        >
          {props.listingData.mexicoMarketSearch.map((e, i) => (
            <tr key={i} className="text-left">
              <td className={"whitespace-nowrap p-4 w-52"}>{e.name}</td>
              <td className={"whitespace-nowrap p-4 w-72"}>
                {e.SBCarrierRates.length > 0
                  ? e.SBCarrierRates.map((item, i) => (
                    <div key={i}>
                      {item.carrierName}{" "}
                      {e.SBCarrierRates.length > 1 &&
                        i < e.SBCarrierRates.length - 1 &&
                        ","}
                    </div>
                  ))
                  : "-"}
              </td>
              <td className="whitespace-nowrap p-4 w-60 overflow-auto scrollbar-hidden">
                {e.DirectRates.length > 0 ? (
                  <div>
                    {e.DirectRates.map((item, i) => (
                      <span key={i}>
                        {item.domesticState}
                        {e.DirectRates.length > 1 &&
                          i < e.DirectRates.length - 1 &&
                          ", "}
                      </span>
                    ))}
                  </div>
                ) : (
                  "-"
                )}
              </td>
              <td className={"whitespace-nowrap p-4 w-64"}>
                {e.portOfEntry.name}
              </td>
              <td className={"whitespace-nowrap p-4 w-64"}>
                {e.portOfEntry.carriers.length > 0
                  ? e.portOfEntry.carriers.map((item, i) => (
                    <div key={i}>
                      {item.name}{" "}
                      {e.portOfEntry.carriers.length > 1 &&
                        i < e.portOfEntry.carriers.length - 1 &&
                        ","}
                    </div>
                  ))
                  : "-"}
              </td>
              <td className={"whitespace-nowrap p-4 w-64"}>
                {e.portOfEntry.TransloadPartners.length > 0
                  ? e.portOfEntry.TransloadPartners.map((item, i) => (
                    <div key={i}>
                      {item.transloadCarrier == null
                        ? "-"
                        : item.transloadCarrier}
                      {e.portOfEntry.TransloadPartners.length > 1 &&
                        i < e.portOfEntry.TransloadPartners.length - 1 &&
                        ","}
                    </div>
                  ))
                  : "-"}
              </td>
              <td className="whitespace-nowrap py-4 px-7 w-24 flex justify-end items-center">
                <EyeIcon
                  className="w-5 h-5 text-accent cursor-pointer"
                  onClick={() => {
                    props.setPopup(true)
                    setData(e)
                    setMexicoMarketName(e.name)
                  }}
                />
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </>
  )
}
